import { Link } from 'react-router';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  companyMsg,
  contractPeriodMsg,
  deviceSelectionMsg,
  lastModifiedMsg,
  productTypeMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { getContractTermLabel } from '../../common/utils/deviceListFormUtilsCoCo.js';
import {
  DEVICE_LIST_CONTRACT_PERIOD_PARAM,
  DEVICE_LIST_PRODUCT_TYPE_PARAM,
  DEVICE_LIST_STATUS_PARAM,
  getDeviceListPath,
  getDeviceSelectionPath,
  getDeviceSelectionProductTypeLabel,
} from '../../common/utils/deviceListTabsUtilsCoCo.js';
import {
  DeviceListColumnCoCo,
  EMPTY_VALUE,
  filterDeviceListItems,
  getDeviceListStatusLabel,
  isShownDeviceList,
  sortDeviceListItems,
  toDeviceListItem,
} from '../../common/utils/deviceListUtilsCoCo.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { TimestampColumn } from '../CatalogList/TimestampColumn.js';
import { Highlight } from '../Highlight/Highlight.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  ListPagination,
  Table,
  TableUrlParams,
  getItemsPerPageFromOptionsOrDefault,
  tablePaginate,
} from '../Table/index.js';
import { DeviceListsToolbarCoCo } from './DeviceListsToolbarCoCo.js';
import { DeviceSelectionDisclaimerCoCo } from './DeviceSelectionDisclaimerCoCo.js';
import type { CLTableColumn, CLTableRow } from '../../common/types/componentLibrary';
import type { EmployeeProductListResponse } from '../../generated/api/employeeProductListResponse.js';
import type { EmployeeProductSelectionResponse } from '../../generated/api/employeeProductSelectionResponse.js';

// The last option is always shown as "All" in the items per page dropdown
const ITEMS_PER_PAGE_OPTIONS = [DEFAULT_ITEMS_PER_PAGE, Number.MAX_SAFE_INTEGER];

const getColumns = (): CLTableColumn[] => [
  { key: DeviceListColumnCoCo.NAME, label: t.IVJB('Name of device list'), sortable: true, width: '20%' },
  { key: DeviceListColumnCoCo.PRODUCT_TYPE, label: t.R6HP(productTypeMsg), sortable: true },
  { key: DeviceListColumnCoCo.CONTRACT_PERIOD, label: t.D55M(contractPeriodMsg), sortable: true },
  { key: DeviceListColumnCoCo.DEVICE_SELECTION, label: t.AHB4(deviceSelectionMsg), sortable: true },
  { key: DeviceListColumnCoCo.LAST_MODIFIED, label: t.PM1H(lastModifiedMsg), sortable: true },
  { key: DeviceListColumnCoCo.STATUS, label: t.ASQT(statusMsg), sortable: true },
  { key: DeviceListColumnCoCo.COMPANY, label: t.KJTS(companyMsg), sortable: true },
];

export interface DeviceListsCoCoProps {
  employeeProductLists: EmployeeProductListResponse[];
  deviceSelections: EmployeeProductSelectionResponse[];
}

export const DeviceListsCoCo = ({ employeeProductLists, deviceSelections }: DeviceListsCoCoProps) => {
  const { authenticatedUser } = useAuth();
  const {
    [TableUrlParams.SEARCH]: search,
    [TableUrlParams.SORT]: sort,
    [TableUrlParams.ORDER]: order,
    [TableUrlParams.OFFSET]: offset,
    [TableUrlParams.LIMIT]: limit,
    [DEVICE_LIST_PRODUCT_TYPE_PARAM]: productType,
    [DEVICE_LIST_CONTRACT_PERIOD_PARAM]: contractPeriod,
    [DEVICE_LIST_STATUS_PARAM]: status,
  } = useSearchParams<{
    [TableUrlParams.SEARCH]?: string;
    [TableUrlParams.SORT]?: string;
    [TableUrlParams.ORDER]?: string;
    [TableUrlParams.OFFSET]?: string;
    [TableUrlParams.LIMIT]?: string;
    [DEVICE_LIST_PRODUCT_TYPE_PARAM]?: string;
    [DEVICE_LIST_CONTRACT_PERIOD_PARAM]?: string;
    [DEVICE_LIST_STATUS_PARAM]?: string;
  }>();
  const searchTerms = search?.split(' ') ?? [];
  const deviceSelectionNames = new Map(deviceSelections.map(selection => [selection.uuid, selection.name]));
  const items = employeeProductLists
    .filter(isShownDeviceList)
    .map(list =>
      toDeviceListItem(
        list,
        deviceSelectionNames,
        accountMdmId => getCompanyName(authenticatedUser, accountMdmId) || accountMdmId || ''
      )
    );

  const filteredItems = sortDeviceListItems(
    filterDeviceListItems(items, { search, productType, contractPeriod, status }),
    sort,
    order
  );

  // TODO: Pagination is done in FE for now. Check if BE supports pagination for device lists and move it there.
  const itemsPerPage = getItemsPerPageFromOptionsOrDefault(limit, ITEMS_PER_PAGE_OPTIONS);
  const pageItems = itemsPerPage ? tablePaginate(filteredItems, offset ?? '0', itemsPerPage) : filteredItems;

  const rows: CLTableRow[] = pageItems.map(item => {
    const name = <Highlight text={item.name} highlights={searchTerms} />;
    return {
      [DeviceListColumnCoCo.NAME]: item.uuid ? <Link to={getDeviceListPath(item.uuid, item.mdmId)}>{name}</Link> : name,
      [DeviceListColumnCoCo.PRODUCT_TYPE]: getDeviceSelectionProductTypeLabel(item.productType),
      [DeviceListColumnCoCo.CONTRACT_PERIOD]:
        item.contractTerms.length > 0 ? item.contractTerms.map(getContractTermLabel).join(', ') : EMPTY_VALUE,
      // The selection name is found only for a list with a selection uuid
      [DeviceListColumnCoCo.DEVICE_SELECTION]:
        item.deviceSelection && item.deviceSelectionUuid ? (
          <Link to={getDeviceSelectionPath(item.deviceSelectionUuid, item.mdmId)}>
            <Highlight text={item.deviceSelection} highlights={searchTerms} />
          </Link>
        ) : (
          EMPTY_VALUE
        ),
      [DeviceListColumnCoCo.LAST_MODIFIED]: <TimestampColumn timestamp={item.lastModified} />,
      [DeviceListColumnCoCo.STATUS]: item.status ? getDeviceListStatusLabel(item.status) : EMPTY_VALUE,
      [DeviceListColumnCoCo.COMPANY]: item.company || EMPTY_VALUE,
    };
  });

  return (
    <div className="of-device-lists-tab-coco" data-testid="device-lists-tab-coco">
      <DeviceSelectionDisclaimerCoCo />
      <DeviceListsToolbarCoCo items={items} />
      <Table columns={getColumns()} rows={rows} valignText="top" noItemsText={t.TFX1('No device lists')} />
      <ListPagination itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS} totalItems={filteredItems.length} />
    </div>
  );
};
